import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '../../context/UserContext';
import Toggle from 'react-toggle';
import { RoomObjectType } from '../../types/room';
import { useNFTContext } from '../../context/NFTContext';
import RoomChangeInfo from './RoomChangeInfo';
import { useFloorContext } from '../../context/FloorContext';
import { getBoundingBox } from '../../utils/floor_utils';
import { useTargetContext } from '../../context/TargetContext';
import { useRoomContext, useRoomsByOwner } from '../../context/RoomContext';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { formatRoomName } from '../../utils/room_utils';

import EditSuccessDialog from './dialogs/EditSuccessDialog';
import EditErrorDialog from './dialogs/EditErrorDialog';

const SidePanelMyRooms = ({
  handleNavigateToSelectedNft,
  handleStartPlacingNFT,
  onPanelClose,
}: {
  handleNavigateToSelectedNft: (nft: RoomObjectType) => void;
  handleStartPlacingNFT: (nft: RoomObjectType) => void;
  onPanelClose: () => void;
}) => {
  const { target } = useTargetContext();

  const { wallet_address, user_name, connected, connect } = useUserContext();
  const { move } = useRoomContext();

  const {
    rooms,
    loading: loadingRooms,
    refresh: refreshMyRooms,
  } = useRoomsByOwner(connected ? wallet_address : '');

  useEffect(() => {
    refreshMyRooms();
  }, [refreshMyRooms]);

  const { currentFloor, fetchCurrentFloorData } = useFloorContext();
  const {
    filteredNFTs,
    setFilteredNFTs,
    setSelectedNFT,
    setNFTPlacingMode,
    isNFTPlacingMode,
    nftPlacingModel,
    nftBeingPlaced,
    nftPlacingCells,
    isNFTPlacingValid,
    nftPlaceableCellsAmountOnCurrentFloor,
  } = useNFTContext();

  const toggleShowMyRooms = useCallback(() => {
    if (filteredNFTs.length === 0) {
      setSelectedNFT(undefined);
      setFilteredNFTs(rooms.filter((x) => x.isPlaced));
    } else {
      setFilteredNFTs([]);
    }
  }, [filteredNFTs.length, rooms, setFilteredNFTs, setSelectedNFT]);

  const [width, height] = useMemo(
    () =>
      nftPlacingModel
        ? [nftPlacingModel.widthX, nftPlacingModel.heightZ]
        : [0, 0],
    [nftPlacingModel]
  );

  const [loadingPlacement, setLoadingPlacement] = useState(false);

  const onNFTPlace = useCallback(async () => {
    if (!isNFTPlacingValid || !nftBeingPlaced) {
      return;
    }

    setLoadingPlacement(true);

    const { startX, startY, endX, endY } = getBoundingBox(nftPlacingCells);

    target === 'dev' &&
      console.log(
        'Place NFT request:',
        { startX, startY, endX, endY },
        'cells'
      );

    const room = await move(
      nftBeingPlaced.roomId,
      currentFloor,
      startX,
      startY,
      endX,
      endY
    );

    if ('result' in room) {
      target === 'dev' && console.log('Not placed due to error');
      setErrorDialogMessage(room.error);
      setShowErrorDialog(true);
    } else {
      target === 'dev' && console.log('Placed succesfully');
      setShowSuccessDialog(true);
    }

    setNFTPlacingMode(false);
    setLoadingPlacement(false);

    fetchCurrentFloorData(currentFloor);
    refreshMyRooms();
  }, [
    currentFloor,
    fetchCurrentFloorData,
    isNFTPlacingValid,
    move,
    nftBeingPlaced,
    nftPlacingCells,
    refreshMyRooms,
    setNFTPlacingMode,
    target,
  ]);

  useEffect(() => {
    if (!isNFTPlacingMode) {
      return;
    }

    fetchCurrentFloorData(currentFloor);
  }, [currentFloor, fetchCurrentFloorData, isNFTPlacingMode]);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    setSelectedNFT(undefined);
  }, [setSelectedNFT]);

  // PLACE ROOM PANEL
  if (isNFTPlacingMode && nftPlacingModel && nftBeingPlaced) {
    return (
      <>
        <div className="side-panel-my-rooms-header-container">
          <div>
            <div className="side-panel-my-rooms-header">Place or move room</div>
            <div className="side-panel-my-rooms-username">{user_name}</div>
          </div>
        </div>
        <div className="side-panel-my-rooms-placed-room-container">
          <div className="side-panel-my-rooms-placed-room-name">
            {formatRoomName(nftBeingPlaced)}
          </div>
          <div
            className="side-panel-my-rooms-placed-room-image"
            style={{
              background: nftBeingPlaced.imageUrl
                ? `url(${nftBeingPlaced.imageUrl}) no-repeat center/cover`
                : nftBeingPlaced.color,
            }}
          />
          <div className="side-panel-my-rooms-placed-room-description ">
            This room is currently a{' '}
            <span style={{ textTransform: 'capitalize' }}>
              {nftBeingPlaced.roomType}
            </span>
            , which needs <b>{width * height} map tiles of space</b>. Select an
            area on the map with enough free space to move the room.
          </div>
          <div className="side-panel-my-rooms-placed-room-size-badge">
            {/* TODO: should be dynamic */}
            <div className="side-panel-my-rooms-placed-room-size-letter">L</div>
            <div className="side-panel-my-rooms-placed-room-size-name">
              <span style={{ textTransform: 'capitalize' }}>
                {nftBeingPlaced.roomType}
              </span>
            </div>
            <div className="side-panel-my-rooms-placed-room-size-badge-tiles">
              {width * height} map tiles
            </div>
          </div>
        </div>
        <div>
          {nftBeingPlaced.isPlaced ? (
            <RoomChangeInfo
              type={'move'}
              color={'blue'}
              current={true}
              title={`Floor ${nftBeingPlaced.roomFloor}`}
            >
              <>
                This room currently takes up{' '}
                {(nftBeingPlaced.endX - nftBeingPlaced.startX + 1) *
                  (nftBeingPlaced.endY - nftBeingPlaced.startY + 1)}{' '}
                tiles in the hotel.
              </>
            </RoomChangeInfo>
          ) : (
            <RoomChangeInfo
              type={'move'}
              color={'blue'}
              current={true}
              title="Not yet placed"
            >
              <>
                This room has not yet been placed into the hotel and can
                therefore be positioned free of charge.
              </>
            </RoomChangeInfo>
          )}
          <RoomChangeInfo
            type={'move'}
            color={isNFTPlacingValid ? 'green' : 'red'}
            current={false}
            price={
              isNFTPlacingValid
                ? `${nftPlacingModel.moveCost} NFTCr`
                : 'Unavailable'
            }
            title={`Floor ${currentFloor}`}
          >
            {isNFTPlacingValid ? (
              <>You have selected {width * height} available tiles.</>
            ) : nftPlaceableCellsAmountOnCurrentFloor > 0 ? (
              <>One or more of your selected tiles are currently unavailable.</>
            ) : (
              <>
                There are no available tiles on current floor for this type of
                room
              </>
            )}
          </RoomChangeInfo>
        </div>
        <div className="side-panel-action-buttons-container vertical">
          <button
            className={`side-panel-action-button ${
              !isNFTPlacingValid ? 'disabled' : ''
            }`}
            onClick={() => onNFTPlace()}
            style={{ marginBottom: 15 }}
            disabled={!isNFTPlacingValid || loadingPlacement}
          >
            Place or move room
          </button>
          <button
            className="side-panel-action-button close"
            onClick={() => onPanelClose()}
            disabled={loadingPlacement}
          >
            Close
          </button>
        </div>
      </>
    );
  }

  // MY ROOMS LIST
  return (
    <>
      {showSuccessDialog && (
        <EditSuccessDialog onClose={() => setShowSuccessDialog(false)} />
      )}
      {showErrorDialog && (
        <EditErrorDialog
          message={errorDialogMessage}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorDialogMessage(undefined);
          }}
        />
      )}
      <div className="side-panel-my-rooms-header-container">
        <div>
          <div className="side-panel-my-rooms-header">My rooms</div>
          <div className="side-panel-my-rooms-username">{user_name}</div>
        </div>
        <div>
          <div className="side-panel-my-rooms-show-on-map">Show on map</div>
          <div className="side-panel-my-rooms-toggle-wrapper">
            <Toggle
              name="show-my-rooms"
              value="boolean"
              checked={filteredNFTs.length > 0}
              onChange={toggleShowMyRooms}
            />
          </div>
        </div>
      </div>
      <div className="side-panel-my-rooms-list-container">
        {!connected ? (
          <div className="side-panel-loading">
            Please connect your wallet using Metamask to view your rooms.
          </div>
        ) : rooms.length === 0 ? (
          loadingRooms ? (
            <div className="side-panel-loading">
              <AiOutlineLoading3Quarters
                color="grey"
                size={40}
                className="loading-icon"
              />
            </div>
          ) : (
            <div className="side-panel-loading">
              You do not own any Habbo X Rooms or Habbo Avatars.
            </div>
          )
        ) : (
          <div className="side-panel-my-rooms-list">
            {rooms.map((nft, i) => (
              <div
                key={nft.roomId + '-' + i}
                className="side-panel-my-rooms-list-item"
                onClick={() => {
                  if (nft.isPlaced) {
                    handleNavigateToSelectedNft(nft);
                  } else {
                    handleStartPlacingNFT(nft);
                  }
                }}
              >
                <div
                  className="side-panel-my-rooms-list-item-room-image"
                  style={{
                    background:
                      nft.cleanImageUrl || nft.imageUrl
                        ? `url(${
                            nft.cleanImageUrl || nft.imageUrl
                          }) no-repeat center/cover`
                        : nft.color,
                  }}
                ></div>
                <div>
                  <div className="side-panel-my-rooms-list-item-room-name">
                    {formatRoomName(nft)}
                  </div>
                  <div className="side-panel-my-rooms-list-item-room-floor">
                    {nft.isPlaced ? (
                      <>
                        Floor {nft.roomFloor} - {nft.roomType}
                      </>
                    ) : (
                      <span style={{ color: '#BF8484' }}>Unplaced</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="side-panel-action-buttons-container vertical">
        {connected ? (
          <button
            className="side-panel-action-button close"
            onClick={() => onPanelClose()}
          >
            Close
          </button>
        ) : (
          <button
            className="side-panel-action-button close"
            onClick={() => connect()}
          >
            Connect Wallet
          </button>
        )}
      </div>
    </>
  );
};

export default SidePanelMyRooms;
